/**
 * 🎯 UNIVERSAL Q&A ELEMENT 2025
 * 
 * Accordion-Style Q&A Component mit:
 * - Expandable/Collapsible Fragen
 * - Editierbare Fragen & Antworten (via TipTap)
 * - Floating Toolbar für Styling
 * - Add/Remove Q&As
 * - Responsive Design
 */

import React, { useState, useRef } from 'react';
import TipTapInlineEditor from './TipTapInlineEditor';
import QAFloatingToolbar from './QAFloatingToolbar';
import './UniversalQAElement.css';

function UniversalQAElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {},
    fonts = {},
    availableFonts = [],
    currentlyEditingElementId = null,
    onEditingChange = () => {}
}) {
    // 🎯 State für geöffnete Q&As (Accordion)
    const [openQAIndex, setOpenQAIndex] = useState(null);
    
    // 🎯 State für Toolbar Visibility
    const [showToolbar, setShowToolbar] = useState(false);
    
    // 🎯 Ref für Position Tracking
    const containerRef = useRef(null);
    
    // 🎯 Q&As aus Element Data
    const qaItems = element.qaItems || [];
    
    // 📝 Default Q&A für neue Items
    const createDefaultQA = () => ({
        id: `qa-${Date.now()}`,
        question: '<p>What is your question?</p>',
        answer: '<p>Enter your answer here...</p>',
        visible: true
    });
    
    // ✅ Add new Q&A
    const handleAddQA = () => {
        const newQA = createDefaultQA();
        const updatedItems = [...qaItems, newQA];
        onUpdate({ ...element, qaItems: updatedItems });
        // ✅ Öffne die neu hinzugefügte Q&A
        setOpenQAIndex(updatedItems.length - 1);
    };
    
    // ❌ Delete Q&A
    const handleDeleteQA = (qaId) => {
        const updatedItems = qaItems.filter(qa => qa.id !== qaId);
        onUpdate({ ...element, qaItems: updatedItems });
    };
    
    // 🔄 Update Q&A
    const handleUpdateQA = (qaId, field, value) => {
        const updatedItems = qaItems.map(qa => 
            qa.id === qaId ? { ...qa, [field]: value } : qa
        );
        onUpdate({ ...element, qaItems: updatedItems });
    };
    
    // 🎨 Toggle Accordion (funktioniert jetzt auch im Edit Mode!)
    const toggleQA = (index) => {
        setOpenQAIndex(openQAIndex === index ? null : index);
    };
    
    // 🎨 Active Text Alignment (responsive)
    const activeTextAlign = isMobileLayout ?
        (element.qaTextAlignMobile ?? element.qaTextAlign ?? 'left') :
        (element.qaTextAlign ?? 'left');
    
    // 🎨 Styling aus Element Data
    const qaContainerStyle = {
        // Background
        backgroundColor: element.qaBackgroundColor || '#ffffff',
        // Border
        border: element.qaBorderEnabled ? 
            `${element.qaBorderWidth || 1}px solid ${element.qaBorderColor || '#e0e0e0'}` : 
            'none',
        borderRadius: isMobileLayout ? 
            `${element.qaBorderRadiusMobile ?? element.qaBorderRadius ?? 8}px` :
            `${element.qaBorderRadius ?? 8}px`,
        // Shadow
        boxShadow: element.qaShadowEnabled ? 
            `0 ${element.qaShadowDistance || 4}px ${element.qaShadowIntensity || 15}px rgba(0, 0, 0, ${(element.qaShadowOpacity || 15) / 100})` :
            'none',
        // Spacing
        padding: isMobileLayout ?
            `${element.qaPaddingMobile ?? element.qaPadding ?? 12}px` :
            `${element.qaPadding ?? 16}px`,
        gap: `${element.qaGap ?? 12}px`
    };
    
    // 🎨 Individual Q&A Item Style
    const getQAItemStyle = (index) => {
        const isFirstItem = index === 0;
        
        // ✅ Separator Line Mode - Top border beim ersten, bottom border bei allen
        if (element.qaSeparatorLineMode) {
            return {
                backgroundColor: element.qaItemBackgroundColor || 'transparent',
                border: 'none',
                borderTop: isFirstItem ? 
                    `${element.qaSeparatorLineWidth || 1}px solid ${element.qaSeparatorLineColor || '#e0e0e0'}` :
                    'none',
                borderBottom: `${element.qaSeparatorLineWidth || 1}px solid ${element.qaSeparatorLineColor || '#e0e0e0'}`,
                borderRadius: 0,
                boxShadow: 'none',
                padding: isMobileLayout ?
                    `${element.qaItemPaddingMobile ?? element.qaItemPadding ?? 16}px` :
                    `${element.qaItemPadding ?? 16}px`,
                textAlign: activeTextAlign
            };
        }
        
        // ✅ Normal Mode - Full Border/Shadow
        return {
            backgroundColor: element.qaItemBackgroundColor || '#f8f9fa',
            border: element.qaItemBorderEnabled ? 
                `${element.qaItemBorderWidth || 1}px solid ${element.qaItemBorderColor || '#e0e0e0'}` : 
                'none',
            borderRadius: isMobileLayout ?
                `${element.qaItemBorderRadiusMobile ?? element.qaItemBorderRadius ?? 6}px` :
                `${element.qaItemBorderRadius ?? 6}px`,
            boxShadow: element.qaItemShadowEnabled ?
                `0 ${element.qaItemShadowDistance || 2}px ${element.qaItemShadowIntensity || 8}px rgba(0, 0, 0, ${(element.qaItemShadowOpacity || 10) / 100})` :
                'none',
            padding: isMobileLayout ?
                `${element.qaItemPaddingMobile ?? element.qaItemPadding ?? 16}px` :
                `${element.qaItemPadding ?? 16}px`,
            textAlign: activeTextAlign
        };
    };
    
    return (
        <div 
            ref={containerRef}
            className={`universal-qa-element-wrapper ${isEditMode ? 'edit-mode' : ''}`}
            onClick={() => {
                if (isEditMode && onSelect) {
                    onSelect();
                    setShowToolbar(true);
                }
            }}
        >
            {/* 🎨 Floating Toolbar */}
            {isEditMode && showToolbar && (
                <QAFloatingToolbar
                    isEditMode={isEditMode}
                    isVisible={showToolbar}
                    
                    // Container Styling
                    backgroundColor={element.qaBackgroundColor}
                    borderEnabled={element.qaBorderEnabled}
                    borderWidth={element.qaBorderWidth}
                    borderColor={element.qaBorderColor}
                    borderRadius={element.qaBorderRadius}
                    borderRadiusMobile={element.qaBorderRadiusMobile}
                    shadowEnabled={element.qaShadowEnabled}
                    shadowIntensity={element.qaShadowIntensity}
                    shadowDistance={element.qaShadowDistance}
                    shadowOpacity={element.qaShadowOpacity}
                    padding={element.qaPadding}
                    paddingMobile={element.qaPaddingMobile}
                    gap={element.qaGap}
                    textAlign={element.qaTextAlign}
                    textAlignMobile={element.qaTextAlignMobile}
                    
                    // Item Styling
                    itemBackgroundColor={element.qaItemBackgroundColor}
                    itemBorderEnabled={element.qaItemBorderEnabled}
                    itemBorderWidth={element.qaItemBorderWidth}
                    itemBorderColor={element.qaItemBorderColor}
                    itemBorderRadius={element.qaItemBorderRadius}
                    itemBorderRadiusMobile={element.qaItemBorderRadiusMobile}
                    itemShadowEnabled={element.qaItemShadowEnabled}
                    itemShadowIntensity={element.qaItemShadowIntensity}
                    itemShadowDistance={element.qaItemShadowDistance}
                    itemShadowOpacity={element.qaItemShadowOpacity}
                    itemPadding={element.qaItemPadding}
                    itemPaddingMobile={element.qaItemPaddingMobile}
                    separatorLineMode={element.qaSeparatorLineMode}
                    separatorLineWidth={element.qaSeparatorLineWidth}
                    separatorLineColor={element.qaSeparatorLineColor}
                    
                    // Icon Settings
                    iconStyle={element.qaIconStyle}
                    iconColor={element.qaIconColor}
                    
                    // Callbacks
                    onStyleChange={(styles) => {
                        // ✅ Handle Add Q&A command
                        if (styles.addNewQA) {
                            handleAddQA();
                            // Entferne das Flag, damit es nicht gespeichert wird
                            const { addNewQA, ...otherStyles } = styles;
                            return;
                        }
                        onUpdate({ ...element, ...styles });
                    }}
                    colors={colors}
                    onClose={() => setShowToolbar(false)}
                    containerRef={containerRef}
                    isMobileLayout={isMobileLayout}
                />
            )}
            
            {/* 🎯 Q&A Container */}
            <div className="qa-container" style={qaContainerStyle}>
                {/* 📝 Q&A Items */}
                {qaItems.map((qa, index) => (
                    <div 
                        key={qa.id}
                        className={`qa-item ${openQAIndex === index ? 'open' : ''} ${isEditMode ? 'edit-mode' : ''}`}
                        style={getQAItemStyle(index)}
                    >
                        {/* ❌ Delete Button (nur im Edit Mode) */}
                        {isEditMode && (
                            <button
                                className="qa-delete-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteQA(qa.id);
                                }}
                                title="Delete Q&A"
                            >
                                <i className="fas fa-trash"></i>
                            </button>
                        )}
                        
                        {/* ❓ Question Header */}
                        <div className="qa-question-header">
                            {/* Icon - Klickbar für Toggle */}
                            <div 
                                className={`qa-icon ${openQAIndex === index ? 'rotated' : ''} ${element.qaIconStyle ? `icon-style-${element.qaIconStyle}` : ''}`}
                                style={{
                                    color: element.qaIconColor || colors.accentColor || '#5C80BC'
                                }}
                                onClick={() => toggleQA(index)}
                            >
                                {/* Plus/Minus Styles - Icons wechseln */}
                                {element.qaIconStyle === 'plus-minus' && (
                                    <i className={`fas ${openQAIndex === index ? 'fa-minus' : 'fa-plus'}`}></i>
                                )}
                                {element.qaIconStyle === 'circle-plus' && (
                                    <i className={`fas ${openQAIndex === index ? 'fa-minus-circle' : 'fa-plus-circle'}`}></i>
                                )}
                                
                                {/* Rotate Styles - Icons rotieren */}
                                {element.qaIconStyle === 'chevron' && (
                                    <i className="fas fa-chevron-down"></i>
                                )}
                                {element.qaIconStyle === 'angle' && (
                                    <i className="fas fa-angle-down"></i>
                                )}
                                {element.qaIconStyle === 'angle-double' && (
                                    <i className="fas fa-angle-double-down"></i>
                                )}
                                {element.qaIconStyle === 'caret-sharp' && (
                                    <i className="fas fa-caret-down qa-icon-sharp"></i>
                                )}
                                {element.qaIconStyle === 'long-arrow' && (
                                    <i className="fas fa-long-arrow-alt-down qa-icon-thin"></i>
                                )}
                                {element.qaIconStyle === 'arrow-thin' && (
                                    <i className="fas fa-arrow-down qa-icon-thin"></i>
                                )}
                                {element.qaIconStyle === 'chevron-circle' && (
                                    <i className="fas fa-chevron-circle-down"></i>
                                )}
                                
                                {/* Default: Chevron */}
                                {!element.qaIconStyle && (
                                    <i className="fas fa-chevron-down"></i>
                                )}
                            </div>
                            
                            {/* Question Text - Klick stoppt Propagation im Edit Mode */}
                            <div 
                                className="qa-question-text"
                                onClick={(e) => {
                                    // Im Edit Mode: Click auf Text editiert, togglet aber nicht
                                    if (isEditMode) {
                                        e.stopPropagation();
                                    } else {
                                        toggleQA(index);
                                    }
                                }}
                            >
                                <TipTapInlineEditor
                                    value={qa.question || '<p>What is your question?</p>'}
                                    onChange={(newValue) => handleUpdateQA(qa.id, 'question', newValue)}
                                    isEditMode={isEditMode}
                                    placeholder="Enter question..."
                                    element="div"
                                    enableRichText={true}
                                    showFloatingToolbar={true}
                                    colors={colors}
                                    availableFonts={availableFonts}
                                    brandFonts={fonts}
                                    onSelect={() => {
                                        if (onEditingChange) {
                                            onEditingChange(qa.id);
                                        }
                                    }}
                                    isCurrentlyEditing={currentlyEditingElementId === qa.id}
                                    onEditingStart={() => onEditingChange(qa.id)}
                                    onEditingEnd={() => onEditingChange(null)}
                                    style={{
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        color: '#333333'
                                    }}
                                />
                            </div>
                        </div>
                        
                        {/* 💬 Answer Content */}
                        <div className={`qa-answer-content ${(openQAIndex === index || currentlyEditingElementId === `${qa.id}-answer`) ? 'show' : ''}`}>
                            <TipTapInlineEditor
                                value={qa.answer || '<p>Enter your answer here...</p>'}
                                onChange={(newValue) => handleUpdateQA(qa.id, 'answer', newValue)}
                                isEditMode={isEditMode}
                                placeholder="Enter answer..."
                                element="div"
                                enableRichText={true}
                                showFloatingToolbar={true}
                                colors={colors}
                                availableFonts={availableFonts}
                                brandFonts={fonts}
                                onSelect={() => {
                                    // ✅ Accordion öffnen wenn Antwort editiert wird
                                    if (openQAIndex !== index) {
                                        setOpenQAIndex(index);
                                    }
                                    if (onEditingChange) {
                                        onEditingChange(`${qa.id}-answer`);
                                    }
                                }}
                                isCurrentlyEditing={currentlyEditingElementId === `${qa.id}-answer`}
                                onEditingStart={() => {
                                    // ✅ Accordion öffnen beim Start des Editierens
                                    if (openQAIndex !== index) {
                                        setOpenQAIndex(index);
                                    }
                                    onEditingChange(`${qa.id}-answer`);
                                }}
                                onEditingEnd={() => onEditingChange(null)}
                                style={{
                                    fontSize: '14px',
                                    color: '#666666',
                                    lineHeight: '1.6'
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default UniversalQAElement;

